import React from 'react';
import type { AttentionItem, Tone } from '../../domain/types';
import type { AttentionItemWorkContextMembership } from '../../domain/radarThreads';
import {
  BrandIcon,
  Badge,
  WorkContextChip,
  ViewContextButton,
  ViewDetailButton,
  CardExpandToggle,
} from '../../ui/common';
import { githubPrBadgeLabel } from './githubCardVisuals';
import { githubIdentityFromAttentionItem } from '../../domain/projectCardIdentity';

interface GitHubCardHeaderProps {
  item: AttentionItem;
  statusTone: Tone;
  failedChecks: number;
  isExpanded: boolean;
  workContext?: AttentionItemWorkContextMembership;
  onCardClick: () => void;
  onToggleExpanded: (e: React.MouseEvent) => void;
  onNavigateToJira?: (key: string) => void;
  onNavigateToContext?: () => void;
  onOpenContext?: () => void;
  onOpenDetail?: () => void;
}

function HeaderBadges({
  item,
  statusTone,
  failedChecks,
}: {
  item: AttentionItem;
  statusTone: Tone;
  failedChecks: number;
}): React.ReactElement {
  return (
    <>
      <Badge label={githubPrBadgeLabel(item)} tone={item.merged ? 'ok' : statusTone} />
      {failedChecks > 0 && !item.merged ? (
        <Badge
          label={`${failedChecks} ${failedChecks === 1 ? 'verificación fallida' : 'verificaciones fallidas'}`}
          tone="bad"
        />
      ) : null}
      {item.merged ? <Badge label="Fusionado" tone="ok" /> : null}
    </>
  );
}

function TicketChip({
  ticketKey,
  onNavigate,
}: {
  ticketKey: string;
  onNavigate?: (key: string) => void;
}): React.ReactElement {
  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 999,
    cursor: onNavigate ? 'pointer' : 'default',
    background: 'var(--z-accent-wash)',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--z-accent-ink)',
    border: 'none',
  };

  if (onNavigate) {
    return (
      <button
        type="button"
        data-testid="github-card-ticket-chip"
        onClick={(e) => {
          e.stopPropagation();
          onNavigate(ticketKey);
        }}
        style={style}
      >
        {ticketKey}
      </button>
    );
  }

  return (
    <span data-testid="github-card-ticket-chip" style={style}>
      {ticketKey}
    </span>
  );
}

export const GitHubCardHeader: React.FC<GitHubCardHeaderProps> = ({
  item,
  statusTone,
  failedChecks,
  isExpanded,
  workContext,
  onCardClick,
  onToggleExpanded,
  onNavigateToJira,
  onNavigateToContext,
  onOpenContext,
  onOpenDetail,
}) => {
  const { projectPrLabel, linkedTicketKey } = githubIdentityFromAttentionItem(item);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick();
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8, flexWrap: 'wrap' }}
      >
        <BrandIcon source="github" size={15} />
        <span
          data-testid="github-card-project-pr"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--z-muted-2)',
          }}
        >
          {projectPrLabel || item.primary}
        </span>
        {linkedTicketKey ? (
          <TicketChip ticketKey={linkedTicketKey} onNavigate={onNavigateToJira} />
        ) : null}
        {workContext ? (
          <WorkContextChip membership={workContext} onNavigate={onNavigateToContext} />
        ) : null}
        <HeaderBadges item={item} statusTone={statusTone} failedChecks={failedChecks} />
        {onOpenContext ? (
          <ViewContextButton
            onClick={(e) => {
              e.stopPropagation();
              onOpenContext();
            }}
          />
        ) : null}
        {onOpenDetail ? (
          <ViewDetailButton
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail();
            }}
          />
        ) : null}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--z-dim)' }}>{item.age}</span>
        <CardExpandToggle
          isExpanded={isExpanded}
          onClick={onToggleExpanded}
          ariaLabel={isExpanded ? 'Contraer PR' : 'Expandir PR'}
        />
      </div>

      <h3
        style={{
          margin: '0 0 9px',
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1.4,
          letterSpacing: '-0.015em',
          color: 'var(--z-text-2)',
          textWrap: 'pretty',
        }}
      >
        {item.title}
      </h3>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="var(--z-accent-3)"
          style={{ flex: '0 0 12px', marginTop: 3 }}
        >
          <path d="M12 2l1.9 6.1L20 10l-6.1 1.9L12 18l-1.9-6.1L4 10l6.1-1.9z" />
        </svg>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.55,
            color: 'var(--z-muted-3)',
            textWrap: 'pretty',
          }}
        >
          {item.ai}
        </p>
      </div>
    </div>
  );
};
